import { Honeybadger } from '@honeybadger-io/react';
import RuntimeEnvironment from 'config/runtime';

// Detect transient browser-side no-response network failures (CONTENTLY-3919,
// ka#60402521). True ONLY for the no-`response` connectivity classes — axios
// `Network Error`/`ERR_NETWORK`, request aborts/cancellations (`AbortError`,
// `ERR_CANCELED`), the native-`fetch` no-response throw (`TypeError: Failed to
// fetch` on Chromium / `TypeError: Load failed` on Safari/WebKit), and the
// offline case. An axios error that carries a `response` (4xx/5xx) means the
// server answered and is a real, actionable signal — this returns false for
// those, preserving #19057 (422) and #19549 (403). Exported (pure, no side
// effects on import) so the suppression contract can be unit-tested directly
// without rendering the config component.
//
// The native-`fetch` signatures were added for HB 131651005: a single
// `TypeError: Failed to fetch` on /account/activity (portfolio subdomain) that
// came from the GLOBAL unhandled-rejection instrumentation (the page's own
// catch in ActivityPage.tsx only console.errors, it doesn't notify). The SPA
// calls native `fetch()` directly in ~15 places (useEditingUsersPolling,
// useV1Autosave, buildTokenRefreshLink, several page loaders); when one rejects
// with no `.catch`, HB logs `Failed to fetch` — the exact no-response class the
// axios regexes below already suppress, just from the fetch API instead of
// axios. NOT a host-split bug: API JSON paths route fine on portfolio
// subdomains (see PlatformLinkBuilder) and sibling /account/*.json pages do not
// storm. `Failed to fetch` is the browser's generic no-response string, so any
// genuine app error still surfaces a real message and is not matched here.
export const isNoResponseNetworkError = (notice: any): boolean => {
  const message: string = notice?.message || '';
  // A 4xx/5xx response means the server answered — keep it (real signal).
  const errObj = notice?.context?.error ?? notice?.error;
  const hasHttpResponse =
    !!errObj?.response ||
    !!errObj?.status ||
    /\b(4\d{2}|5\d{2})\b/.test(notice?.context?.status?.toString?.() || '');
  if (hasHttpResponse) return false;

  const code: string = errObj?.code || notice?.context?.code || '';
  return (
    /AxiosError:?\s*Network Error/i.test(message) ||
    /\bNetwork Error\b/i.test(message) ||
    /\bERR_NETWORK\b/i.test(message) ||
    /\bERR_CANCELED\b/i.test(message) ||
    /\b(AbortError|The user aborted a request|signal is aborted)\b/i.test(message) ||
    // Native-fetch no-response throw — ANCHORED to the browser's exact bare
    // string (optionally klass-prefixed). The browser throws precisely
    // `TypeError: Failed to fetch` (Chromium) / `Load failed` (Safari) with
    // NOTHING appended. App loaders, by contrast, throw
    // `new Error("Failed to fetch <noun>: ${res.status}")` for an actionable
    // non-OK HTTP response whose status is in the MESSAGE (not on
    // error.response/.status, so `hasHttpResponse` above can't catch it). A
    // loose substring match would wrongly suppress those real failures
    // (PR #790 review, Cursor Bugbot) — anchoring keeps only the bare transport
    // error.
    /^(?:TypeError:\s*)?Failed to fetch\.?$/i.test(message.trim()) ||
    /^(?:TypeError:\s*)?Load failed\.?$/i.test(message.trim()) ||
    code === 'ERR_NETWORK' ||
    code === 'ERR_CANCELED' ||
    (typeof navigator !== 'undefined' && navigator.onLine === false)
  );
};

// Drop throws originating entirely inside a browser extension (HB 133931637); rationale + guards: filter #7 in .claude/rules/honeybadger-suppression.md.
export const isBrowserExtensionOriginError = (notice: any): boolean => {
  const frames = notice?.backtrace;
  // Load-bearing: `[].every()` returns TRUE, so an empty backtrace must not reach the check.
  if (!Array.isArray(frames) || frames.length === 0) return false;
  return frames.every((frame: any) =>
    /^(?:chrome|moz|safari-web)-extension:\/\//i.test(frame?.file ?? '')
  );
};

export default function HoneybadgerConfig() {
  const honeybadger = RuntimeEnvironment.honeybadger;

  if (honeybadger.enabled.toString() !== 'true'){
    console.warn(`Honeybadger not enabled (got ${honeybadger.enabled})`);
    return null;
  }

  const warnSetupSkipped = (envVar: string, msg = 'skipping Honeybadger setup...') => {
    console.warn(`honeybadger.${envVar} undefined (honeybadger is: ${honeybadger})`);
  };

  if (!honeybadger.apiKey) {
    warnSetupSkipped('apiKey');
    return null;
  }

  if (!honeybadger.environment) {
    warnSetupSkipped('environment');
    return null;
  }

  // Set up configuration
  const config: any = {
    apiKey: honeybadger.apiKey,
    environment: honeybadger.environment,
    revision: process.env.REACT_APP_GIT_REVISION,
    enableUncaught: true
  };
  
  // Add a safety wrapper around Honeybadger to catch ProseMirror range errors
  const originalNotify = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (message && typeof message === 'string' && message.match(/Position .* out of range/i)) {
      console.log("Suppressed ProseMirror range error:", message);
      return true; // Prevents the error from propagating
    }
    
    // Call the original handler if it exists
    if (originalNotify) {
      return originalNotify.apply(this, [message, source, lineno, colno, error]);
    }
    
    return false;
  };
  
  Honeybadger.configure(config);

  // Suppress noise from the deprecated V1 (Redactor) and V2 (raw ProseMirror)
  // editors. V3 (TipTap) nodeSize errors must still surface so real defects
  // are visible. EditPage writes editorVersion ('v1'|'v2'|'v3') into HB
  // context per story; precedence mirrors Story.rb (V3 wins, else V2 if
  // editor_v2, else V1). Mirrors the window.onerror rangeerror precedent
  // (PR #404). Tracks: CONTENTLY-3733.
  Honeybadger.beforeNotify((notice) => {
    const editorVersion = notice?.context?.editorVersion;
    if (
      notice?.message &&
      /nodeSize/i.test(notice.message) &&
      (editorVersion === 'v1' || editorVersion === 'v2')
    ) {
      console.warn(
        '[HB] Suppressed legacy editor nodeSize noise:',
        editorVersion,
        notice.message
      );
      return false;
    }
    return true;
  });

  // Suppress structurally-unavoidable y-prosemirror sync-plugin race noise.
  // The throw `RangeError: Applying a mismatched transaction` originates in
  // ProseMirror's EditorState.applyInner when ySyncPlugin reconnects against
  // a buffered offline IndexedDB update or a concurrent peer update — the
  // transaction's `before` no longer matches view.state.doc by the time
  // dispatch runs. Upstream tracked as yjs/y-prosemirror#117 (closed
  // 2026-03-19 as "not planned" by dmonad). The Y.Doc remains the system
  // of record and PM resyncs on the next yjs observer firing.
  //
  // Gated to editorVersion === 'v3' because (a) V1 (Redactor) is not a
  // ProseMirror editor and cannot produce this signature, and (b) V2 is
  // raw ProseMirror without y-prosemirror — V2 could theoretically hit
  // `applyInner`'s mismatched-tx throw via an application bug in
  // src/editor/editor-ui/src/hooks/useProsemirror.ts, but it cannot hit
  // the y-prosemirror sync race specifically (no ySyncPlugin in V2). Any
  // V1 occurrence is impossible; any V2 occurrence is a real defect that
  // should surface. Mirrors the CONTENTLY-3733 nodeSize filter structure
  // immediately above. EditPage.tsx wires `editorVersion` into HB context
  // via useEffect with cleanup to prevent leakage across SPA navigations
  // — see EditPage.tsx around the Honeybadger.setContext call.
  //
  // Trade-off acknowledged: an application-code mismatched-tx bug in V3
  // (e.g. the removeCustomField walk-after-dispatch class of bug — see
  // PR #743 for the canonical example) would also be silenced here. We
  // accept that risk because (a) such bugs remain customer-visible — the
  // dispatch throws before view.updateState, so the operation simply
  // doesn't apply — for foreground user actions, and (b) for background
  // reconciliation paths where the user wouldn't notice silently, the
  // aggregate-summary pattern below still emits one HB notice per
  // session-threshold-crossing so engineering retains visibility into
  // storms. The deliberate choice is to NOT wrap dispatchTransaction at
  // runtime — that would put our code in the CRDT hot path and inherit
  // y-prosemirror's invariants, which is exactly what migrating to
  // managed Tiptap was meant to avoid.
  //
  // TODO(CONTENTLY-3814): remove this filter once Tiptap upgrades past
  // y-prosemirror 1.3.x and the upstream race resolves. Re-check
  // yjs/y-prosemirror#117 quarterly. Tracks: ka#60389966, CONTENTLY-3814,
  // CONTENTLY-2912, CONTENTLY-2574.
  let mismatchedTxSessionCount = 0;
  const SUMMARY_THRESHOLDS = [1, 25, 250];

  Honeybadger.beforeNotify((notice) => {
    const editorVersion = notice?.context?.editorVersion;
    if (
      notice?.message &&
      /Applying a mismatched transaction/i.test(notice.message) &&
      editorVersion === 'v3'
    ) {
      mismatchedTxSessionCount += 1;

      // Emit ONE aggregate notice at threshold crossings so SaaS sees a
      // single fault for the storm (with running count) instead of an
      // alert per occurrence — and so engineering retains visibility for
      // background-reconciliation paths the user can't see. The aggregate
      // notice's message string deliberately does NOT match the suppression
      // regex above, so it bypasses this same filter on re-entry. Deferred
      // via setTimeout(0) to avoid synchronous reentrancy through the
      // beforeNotify chain.
      if (SUMMARY_THRESHOLDS.includes(mismatchedTxSessionCount)) {
        const count = mismatchedTxSessionCount;
        setTimeout(() => {
          try {
            Honeybadger.notify(
              new Error(
                `V3 y-prosemirror noise summary — suppressed mismatched-transaction count: ${count}`
              ),
              {
                tags: 'low-severity-known-race,v3-mismatched-tx',
                fingerprint: 'v3-y-prosemirror-mismatched-tx-aggregate',
                context: { mismatched_tx_session_count: count },
              }
            );
          } catch {
            // Aggregate-emit failure must not affect editor — telemetry only.
          }
        }, 0);
      }

      console.warn(
        '[HB] Suppressed V3 y-prosemirror mismatched-transaction noise (session count:',
        mismatchedTxSessionCount,
        '):',
        notice.message
      );
      return false;
    }
    return true;
  });

  // Suppress structurally-unavoidable Datadog RUM telemetry-transport noise.
  // Signature: klass `window.onunhandledrejection`, message
  // `UnhandledPromiseRejectionWarning: Unspecified reason`. The rejecting
  // promise originates INSIDE the bundled `@datadog/browser-rum` SDK's own
  // telemetry transport (the `POST https://metrics.contently.com/dd/api/v2/rumx`
  // intake beacon), not in application code — confirmed by the masked DD-worker
  // frames (`webkit-masked-url://hidden/`) in the backtrace and by the
  // Infrastructure investigation in CONTENTLY-3890, which tied the alert to a
  // transient `domain-proxy-prod-alb` 4XX spike (00:22–00:24 UTC) that
  // self-recovered with zero ELB 5XX and required no infra fix. The DD RUM
  // `proxy` option in useDatadog.tsx is a pure URL-string builder, so there is
  // NO application-owned fetch to attach a local `.catch()` to — the transport
  // lives entirely within the SDK. This is telemetry-only; the editor and all
  // user-facing functionality are unaffected.
  //
  // Gate is the message itself: "Unspecified reason" is Honeybadger's literal
  // placeholder for a rejection carrying no Error/reason — i.e. nothing to fix
  // by definition. Genuine application bugs surface a real message
  // (`Cannot read properties of ...`, `... is not a function`), so this regex
  // cannot swallow them. Honeybadger groups this signature non-deterministically
  // (it filed identical-backtrace occurrences as BOTH fault #96197676 — chronic,
  // open since 2023, 11.7k+ notices — AND the fresh splinter #130899457 within
  // the same minute), so a per-fault HB "Ignore" is whack-a-mole; dropping the
  // notice client-side here is the only regrouping-proof suppression.
  //
  // Trade-off acknowledged: a genuine reasonless rejection (e.g. application
  // code rejecting a promise with a non-Error value) would also be silenced.
  // Residual risk is mitigated by the aggregate-summary heartbeat below — it
  // emits ONE HB notice per session-threshold-crossing (distinct fingerprint,
  // so it bypasses this filter on re-entry) so engineering retains a volume
  // signal rather than total silence. Mirrors the CONTENTLY-3814
  // mismatched-transaction filter structure immediately above.
  //
  // TODO(CONTENTLY-3889): re-evaluate after the next `@datadog/browser-rum`
  // bump (currently 5.17.1) — if a newer SDK swallows its own transport
  // rejections, remove this filter. Tracks: ka#60396229, CONTENTLY-3889,
  // CONTENTLY-3890, HB faults #130899457 + #96197676.
  let ddTelemetryRejectionCount = 0;
  const DD_SUMMARY_THRESHOLDS = [1, 25, 250];

  Honeybadger.beforeNotify((notice) => {
    if (notice?.message && /Unspecified reason/i.test(notice.message)) {
      ddTelemetryRejectionCount += 1;

      // Emit ONE aggregate notice at threshold crossings so SaaS/engineering
      // keep a volume signal for the suppressed storm. The aggregate message
      // deliberately does NOT match the /Unspecified reason/ regex above, so
      // it bypasses this same filter on re-entry. Deferred via setTimeout(0)
      // to avoid synchronous reentrancy through the beforeNotify chain.
      if (DD_SUMMARY_THRESHOLDS.includes(ddTelemetryRejectionCount)) {
        const count = ddTelemetryRejectionCount;
        setTimeout(() => {
          try {
            Honeybadger.notify(
              new Error(
                `Datadog RUM telemetry rejection noise summary — suppressed unhandledrejection count: ${count}`
              ),
              {
                tags: 'low-severity-known-noise,dd-rum-telemetry',
                fingerprint: 'dd-rum-unhandledrejection-unspecified-aggregate',
                context: { dd_telemetry_rejection_session_count: count },
              }
            );
          } catch {
            // Aggregate-emit failure must not affect the app — telemetry only.
          }
        }, 0);
      }

      console.warn(
        '[HB] Suppressed Datadog RUM telemetry unhandledrejection noise (session count:',
        ddTelemetryRejectionCount,
        '):',
        notice.message
      );
      return false;
    }
    return true;
  });

  // Suppress transient browser-side no-response network failures.
  // Signature: `AxiosError: Network Error` (axios `code: ERR_NETWORK`) and the
  // related no-response connectivity classes — request aborts/cancellations
  // (`AbortError`, axios `ERR_CANCELED`) and offline navigations. These reject
  // with NO `response` (no HTTP status) because the request never reached — or
  // never heard back from — the server: a dropped connection, DNS timeout,
  // captive-portal redirect, browser tab backgrounding, or the user going
  // offline. They are not application defects and there is nothing to fix in
  // code; the next user action simply retries. Yet `errors.ts`'
  // `reportError`/`logError` notify unconditionally and `enableUncaught: true`
  // catches the unhandled rejection, so each blip on the high-traffic
  // `/stories/:id/edits` route escalated to a P1/Showstopper SaaS incident even
  // though the backend logged only HTTP 200s. Recurred 5x with no fix
  // (CONTENTLY-3533/3544/3764/3848/3919). Tracks: ka#60402521, CONTENTLY-3919.
  //
  // SCOPED, not global: an axios error that carries a `response` (4xx/5xx) is a
  // real, actionable signal and is NEVER suppressed here — this preserves the
  // 422 workflow-validation alerting (#19057) and the 403 expired-session
  // handling (#19549). Only the no-`response`/connectivity signature is dropped.
  //
  // Mirrors the CONTENTLY-3814 / CONTENTLY-3889 filter structure above,
  // including the aggregate-summary heartbeat: a genuine broad outage produces a
  // storm of these, so we emit ONE Honeybadger notice per session-threshold
  // crossing (distinct fingerprint, so it bypasses this filter on re-entry) to
  // retain a volume signal rather than total silence. The suppression predicate
  // is the module-level `isNoResponseNetworkError` (exported for unit testing).
  let networkErrorSessionCount = 0;
  const NETWORK_SUMMARY_THRESHOLDS = [1, 25, 250];

  Honeybadger.beforeNotify((notice) => {
    if (isNoResponseNetworkError(notice)) {
      networkErrorSessionCount += 1;

      // Emit ONE aggregate notice at threshold crossings so SaaS/engineering
      // keep a volume signal for the suppressed storm (e.g. a broad outage).
      // The aggregate message deliberately does NOT match the network-error
      // regexes above, so it bypasses this same filter on re-entry. Deferred
      // via setTimeout(0) to avoid synchronous reentrancy through the
      // beforeNotify chain.
      if (NETWORK_SUMMARY_THRESHOLDS.includes(networkErrorSessionCount)) {
        const count = networkErrorSessionCount;
        setTimeout(() => {
          try {
            Honeybadger.notify(
              new Error(
                `Browser no-response network failure noise summary — suppressed count: ${count}`
              ),
              {
                tags: 'low-severity-known-noise,browser-network-error',
                fingerprint: 'network-error-no-response-aggregate',
                context: { network_error_session_count: count },
              }
            );
          } catch {
            // Aggregate-emit failure must not affect the app — telemetry only.
          }
        }, 0);
      }

      console.warn(
        '[HB] Suppressed browser no-response network failure noise (session count:',
        networkErrorSessionCount,
        '):',
        notice?.message
      );
      return false;
    }
    return true;
  });

  // Suppress browser-EXTENSION messaging noise that is not Contently code.
  // Signature: the literal Chromium string "A listener indicated an asynchronous
  // response by returning true, but the message channel closed before a response
  // was received". This is thrown by the browser itself when an extension's
  // `chrome.runtime.onMessage` listener returns `true` (signalling an async
  // `sendResponse`) but the message port closes before it replies — e.g. the tab
  // navigates or the extension's service worker is evicted mid-exchange. It
  // originates entirely outside our bundle: confirmed by an EMPTY backtrace
  // (`backtrace: []`, no Contently frame) and Edge/Chrome UAs (HB 131913488 —
  // Edge `Edg/149`, 1 notice, on /stories/:id/workflow/edit). Our global
  // `enableUncaught`/unhandledrejection instrumentation sweeps up every
  // page-context rejection including ones the page never authored, so an
  // extension hiccup escalates to an HB fault → prod alert → engineering GHI for
  // something no Contently change can fix. This is the single most common
  // false-positive in browser error tracking and is suppressed near-universally.
  //
  // Gate is the exact fixed Chromium constant — no Contently code path ever
  // produces this string, so unlike a broad substring this cannot mask a real
  // app bug (the load-bearing anchor rule in honeybadger-suppression.md). Mirrors
  // the CONTENTLY-3889 "Unspecified reason" filter structure, including the
  // aggregate-summary heartbeat so a genuine storm still emits one volume signal
  // with a distinct fingerprint that bypasses this filter on re-entry.
  let extensionChannelNoiseCount = 0;
  const EXTENSION_SUMMARY_THRESHOLDS = [1, 25, 250];

  Honeybadger.beforeNotify((notice) => {
    if (
      notice?.message &&
      /A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received/i.test(
        notice.message
      )
    ) {
      extensionChannelNoiseCount += 1;

      if (EXTENSION_SUMMARY_THRESHOLDS.includes(extensionChannelNoiseCount)) {
        const count = extensionChannelNoiseCount;
        setTimeout(() => {
          try {
            Honeybadger.notify(
              new Error(
                `Browser-extension message-channel-closed noise summary — suppressed count: ${count}`
              ),
              {
                tags: 'low-severity-known-noise,browser-extension',
                fingerprint: 'browser-extension-message-channel-closed-aggregate',
                context: { extension_channel_noise_session_count: count },
              }
            );
          } catch {
            // Aggregate-emit failure must not affect the app — telemetry only.
          }
        }, 0);
      }

      console.warn(
        '[HB] Suppressed browser-extension message-channel noise (session count:',
        extensionChannelNoiseCount,
        '):',
        notice.message
      );
      return false;
    }
    return true;
  });

  // Filter #6 (comments destroy race, HB 132039103/133253601) — read the anchor rule in
  // .claude/rules/honeybadger-suppression.md before editing; ungating or broadening hides real bugs.
  // TODO(CONTENTLY-3814 family): drop this and the dist patch together once upstream guards it.
  let commentsDestroyRaceCount = 0;
  const COMMENTS_DESTROY_SUMMARY_THRESHOLDS = [1, 25, 250];

  // Chrome names only the leaf ("reading 'extensions'"), indistinguishable by message from our
  // own checkTextAlignExtension read — so gate it on the backtrace, never the message alone.
  const isCommentsExtensionFrame = (notice: any): boolean =>
    (notice?.backtrace ?? []).some((frame: any) =>
      /@tiptap-pro\/extension-comments/.test(frame?.file ?? '')
    );

  Honeybadger.beforeNotify((notice) => {
    const chromeLeafPhrasing =
      /Cannot read properties of null \(reading 'extensions'\)/i.test(notice?.message ?? '') &&
      isCommentsExtensionFrame(notice);

    if (
      (notice?.message &&
        /extensionManager is null|extensionManager\.extensions/i.test(notice.message)) ||
      chromeLeafPhrasing
    ) {
      commentsDestroyRaceCount += 1;

      if (COMMENTS_DESTROY_SUMMARY_THRESHOLDS.includes(commentsDestroyRaceCount)) {
        const count = commentsDestroyRaceCount;
        setTimeout(() => {
          try {
            // The npm RUM SDK publishes itself as window.DD_RUM, not window.datadogRum.
            const w: any = window;
            if (w.DD_RUM && typeof w.DD_RUM.addAction === 'function') {
              w.DD_RUM.addAction('editor.v3.comments_destroy_race_suppressed', {
                comments_destroy_race_session_count: count,
                known_race: 'v3-comments-extension-manager-null',
              });
            }
          } catch {
            // Aggregate-emit failure must not affect the app — telemetry only.
          }
        }, 0);
      }

      console.warn(
        '[HB] Suppressed V3 comments-extension destroy-race noise (session count:',
        commentsDestroyRaceCount,
        '):',
        notice?.message
      );
      return false;
    }
    return true;
  });

  // Filter #7 — extension-origin noise (HB 133931637). Heartbeat to RUM: `Honeybadger.notify` re-opens the fault this closes.
  let extensionOriginNoiseCount = 0;
  const EXTENSION_ORIGIN_SUMMARY_THRESHOLDS = [1, 25, 250];

  Honeybadger.beforeNotify((notice) => {
    if (isBrowserExtensionOriginError(notice)) {
      extensionOriginNoiseCount += 1;

      if (EXTENSION_ORIGIN_SUMMARY_THRESHOLDS.includes(extensionOriginNoiseCount)) {
        const count = extensionOriginNoiseCount;
        // First frame's origin identifies the vendor without leaking a path.
        const extensionId = (notice?.backtrace?.[0]?.file ?? '').split('/')[2] ?? 'unknown';
        setTimeout(() => {
          try {
            // The npm RUM SDK publishes itself as window.DD_RUM, not window.datadogRum.
            const w: any = window;
            if (w.DD_RUM && typeof w.DD_RUM.addAction === 'function') {
              w.DD_RUM.addAction('browser_extension_noise_suppressed', {
                extension_noise_session_count: count,
                extension_id: extensionId,
              });
            }
          } catch {
            // Aggregate-emit failure must not affect the app — telemetry only.
          }
        }, 0);
      }

      console.warn(
        '[HB] Suppressed browser-extension-origin noise (session count:',
        extensionOriginNoiseCount,
        '):',
        notice?.message
      );
      return false;
    }
    return true;
  });

  window.Honeybadger = Honeybadger;

  return null;
}
